/**
 * 🐕 PLAYFUL PUPPY TEMPERAMENT
 * Excitement builds to UNCONTROLLABLE ZOOMIES!
 * Who's a good boy?! WHO'S A GOOD BOY?!
 *
 * Created by Tom & Azura - January 14, 2026
 */

import type { Temperament } from '../types';

export const PlayfulPuppy: Temperament = {
  id: 'playful-puppy',
  name: 'Playful Puppy',
  emoji: '🐕',
  description: 'Sleepy pup builds excitement to FULL ZOOMIES! Uncontrollable joy!',

  // ============================================================
  // MOODS - Building toward ZOOMIES
  // ============================================================

  moods: ['napping', 'waking', 'playful', 'excited', 'zoomies', 'pooped'],
  initialMood: 'napping',

  // ============================================================
  // MOOD TRANSITIONS
  // ============================================================

  moodMatrix: {
    napping: [
      { mood: 'napping', weight: 0.5 },
      { mood: 'waking', weight: 0.5 },
    ],
    waking: [
      { mood: 'napping', weight: 0.1 },
      { mood: 'waking', weight: 0.3 },
      { mood: 'playful', weight: 0.6 },
    ],
    playful: [
      { mood: 'waking', weight: 0.1 },
      { mood: 'playful', weight: 0.35 },
      { mood: 'excited', weight: 0.55 },
    ],
    excited: [
      { mood: 'playful', weight: 0.1 },
      { mood: 'excited', weight: 0.35 },
      { mood: 'zoomies', weight: 0.55 },
    ],
    zoomies: [
      { mood: 'zoomies', weight: 0.6 },
      { mood: 'pooped', weight: 0.4 },
    ],
    pooped: [
      { mood: 'pooped', weight: 0.2 },
      { mood: 'napping', weight: 0.7 },
      { mood: 'waking', weight: 0.1 },
    ],
  },

  // ============================================================
  // MOOD DURATIONS
  // ============================================================

  moodDurations: {
    napping:  { base: 2500, variance: 3000 },
    waking:   { base: 400, variance: 600 },
    playful:  { base: 350, variance: 500 },
    excited:  { base: 250, variance: 400 },
    zoomies:  { base: 150, variance: 250 },
    pooped:   { base: 1200, variance: 1800 },
  },

  moodConstraints: {
    zoomies: {
      minDuration: 2000,
      maxDuration: 5000,
    },
  },

  // ============================================================
  // ENERGY SYSTEM - Builds excitement!
  // ============================================================

  energy: {
    max: 100,
    initial: 0,
    regenRate: 9,

    moodDrain: {
      napping:  -4,   // Resting up for fun!
      waking:   -3,   // Getting energized...
      playful:  -2,   // Building excitement...
      excited:  -1,   // Almost there...
      zoomies:  35,   // ZOOMIES BURN FAST!
      pooped:   -7,   // Tired pupper recovery
    },

    thresholds: {
      exhausted: 8,
      low: 20,
      high: 45,
      explosive: 65,
    },

    restingMoods: ['napping', 'pooped', 'waking', 'playful'],
  },

  // ============================================================
  // EXPRESSIONS
  // ============================================================

  expressions: {
    napping: {
      rotation: { x: -22, y: -25, z: 3 },
      translation: { x: 0, y: 2, z: 0 },
      scale: 0.98,
      transitionMs: 500,
      jitter: 0.5,
      glowIntensity: 0.1,
      glowColor: 'rgba(255, 200, 150, 0.3)',
    },
    waking: {
      rotation: { x: -18, y: -25, z: 0 },
      translation: { x: 0, y: 0, z: 0 },
      scale: 1,
      transitionMs: 200,
      jitter: 3,
      glowIntensity: 0.25,
      glowColor: 'rgba(255, 200, 100, 0.4)',
    },
    playful: {
      rotation: { x: -20, y: -25, z: 0 },
      translation: { x: 0, y: -1, z: 0 },
      scale: 1.01,
      transitionMs: 120,
      jitter: 5,
      glowIntensity: 0.4,
      glowColor: 'rgba(255, 220, 50, 0.55)',
    },
    excited: {
      rotation: { x: -20, y: -25, z: 0 },
      translation: { x: 0, y: -2, z: 0 },
      scale: 1.03,
      transitionMs: 80,
      jitter: 8,
      glowIntensity: 0.65,
      glowColor: 'rgba(255, 180, 50, 0.7)',
    },
    zoomies: {
      rotation: { x: -20, y: -25, z: 0 },
      translation: { x: 0, y: -3, z: 0 },
      scale: 1.06,
      transitionMs: 40,
      jitter: 9,
      glowIntensity: 1,
      glowColor: 'rgba(255, 150, 0, 1)',
      cssClass: 'zoomies',
    },
    pooped: {
      rotation: { x: -25, y: -25, z: 5 },
      translation: { x: 0, y: 4, z: 0 },
      scale: 0.95,
      transitionMs: 600,
      jitter: 0.3,
      glowIntensity: 0.15,
      glowColor: 'rgba(200, 180, 150, 0.3)',
    },
  },

  // ============================================================
  // CORNER BOUNCING (for zoomies mode - RUNNING EVERYWHERE!)
  // ============================================================

  corners: {
    center: {
      transform: { x: 0, y: 0, z: 0 },
      translation: { x: 0, y: 0, z: 0 },
      bulge: { x: 50, y: 50 },
    },
    topLeft: {
      transform: { x: -3, y: 3, z: -2 },
      translation: { x: -7, y: -6, z: 0 },
      bulge: { x: 15, y: 15 },
    },
    topRight: {
      transform: { x: -3, y: -3, z: 2 },
      translation: { x: 7, y: -6, z: 0 },
      bulge: { x: 85, y: 15 },
    },
    bottomLeft: {
      transform: { x: 3, y: 3, z: 2 },
      translation: { x: -7, y: 6, z: 0 },
      bulge: { x: 15, y: 85 },
    },
    bottomRight: {
      transform: { x: 3, y: -3, z: -2 },
      translation: { x: 7, y: 6, z: 0 },
      bulge: { x: 85, y: 85 },
    },
  },

  bouncingMoods: ['zoomies', 'excited'],

  bounceIntervals: {
    zoomies: 90,
    excited: 180,
  },

  // ============================================================
  // STIMULI HANDLERS
  // ============================================================

  stimulusHandlers: {
    time: (_stimulus, currentMood, energy) => {
      if (currentMood === 'napping' && energy < 15) {
        return { energyDelta: 5 };
      }
      if (currentMood === 'napping' && energy >= 15) {
        return { transitionTo: 'waking', energyDelta: 4 };
      }
      if (currentMood === 'waking') {
        return { transitionTo: 'playful', energyDelta: 3 };
      }
      return null;
    },

    poke: (stimulus, currentMood, energy) => {
      const intensity = stimulus.intensity || 1;

      // Pets and attention = MORE EXCITED!
      if (currentMood === 'napping') {
        return { transitionTo: 'waking', energyDelta: 22 * intensity };
      }
      if (currentMood === 'waking') {
        return { transitionTo: 'playful', energyDelta: 22 * intensity };
      }
      if (currentMood === 'playful') {
        return { transitionTo: 'excited', energyDelta: 25 * intensity };
      }
      if (currentMood === 'excited') {
        return { transitionTo: 'zoomies', energyDelta: 30 * intensity, forceDuration: 2000 };
      }
      if (currentMood === 'pooped') {
        return { transitionTo: 'waking', energyDelta: 15 * intensity };
      }
      return { energyDelta: 18 * intensity };
    },

    shake: (stimulus, currentMood, energy) => {
      const intensity = stimulus.intensity || 1;

      // Play time = INSTANT EXCITEMENT!
      if (currentMood === 'napping' || currentMood === 'waking') {
        return { transitionTo: 'excited', energyDelta: 40 * intensity };
      }
      if (currentMood === 'playful' || currentMood === 'excited' || energy > 35) {
        return { transitionTo: 'zoomies', energyDelta: 45 * intensity, forceDuration: 2500 };
      }
      return { transitionTo: 'excited', energyDelta: 35 * intensity };
    },
  },

  // ============================================================
  // MOOD INDICATORS
  // ============================================================

  moodIndicators: {
    napping: {
      emoji: '😴',
      text: 'zzz',
      label: '*dreaming of treats*',
    },
    waking: {
      emoji: '🥱',
      text: '?',
      label: '*stretch stretch*',
    },
    playful: {
      emoji: '🐶',
      text: '!',
      label: '*tail wagging*',
    },
    excited: {
      emoji: '🤩',
      text: '!!',
      label: 'OH BOY OH BOY!',
    },
    zoomies: {
      emoji: '🐕',
      text: '!!!',
      label: '💨 ZOOMIES!!! 💨',
    },
    pooped: {
      emoji: '😮‍💨',
      text: '...',
      label: '*happy panting*',
    },
  },
};
